import { z } from "zod";

import {
  type CalcRequest,
  type CalculatorDetail,
  type CalculatorDetailResponse,
  CalculatorDetailSchema,
  type CalculatorResult,
  CalculatorResultSchema,
} from "@/5-shared/types/Calculator";
import { type Plant, PlantSchema } from "@/5-shared/types/Plant";

import { http } from "./http";
import { validate } from "./validate";

const PlantListSchema = z.array(PlantSchema);

export async function fetchPlants(): Promise<Plant[]> {
  const response = await http.get<Plant[]>("/public_relations/calculator/plants");
  return validate(PlantListSchema, response.data, "fetchPlants");
}

export async function fetchCalculations(
  body: CalcRequest & { plant_hash: string },
): Promise<CalculatorResult> {
  const res = await http.post("/public_relations/calculator", body);
  return validate(CalculatorResultSchema, res.data, "fetchCalculations");
}

export async function fetchFollowup(
  body: CalcRequest & {
    plant_hash: string;
    email: string;
    send_pdf: boolean;
    /** 8-char scenario group key (7 random + scenario digit 1-3); advanced mode only. */
    comparison_key?: string;
  },
): Promise<CalculatorDetail> {
  const res = await http.post("/public_relations/calculator/followup", body);
  return byYear(validate(CalculatorDetailSchema, res.data, "fetchFollowup"));
}

/**
 * Read every `year_N` series in the order of the backend's `years` (numeric N), so
 * the explorer counts and labels years from the API. A key missing from a series
 * reads 0. The lines keep their `year_0` starting point in front, so they are one
 * longer than `years` (see `lineYears`). Applied after
 * `validate` rather than as a Zod transform: production passes a mismatched payload
 * through raw, and it must still reach the explorer as arrays.
 */
function byYear(raw: CalculatorDetailResponse): CalculatorDetail {
  const keys = Object.keys(raw.years).sort((a, b) => Number(a.slice(5)) - Number(b.slice(5)));
  const at = (series: Record<string, number>) => keys.map((key) => series[key] ?? 0);
  return {
    ...raw,
    years: at(raw.years),
    annual_profits_sold_energy: at(raw.annual_profits_sold_energy),
    annual_go_savings: at(raw.annual_go_savings),
    annual_production: at(raw.annual_production),
    energy_prices: at(raw.energy_prices),
    red_line: [raw.red_line.year_0 ?? 0, ...at(raw.red_line)],
    green_line: [raw.green_line.year_0 ?? 0, ...at(raw.green_line)],
  };
}
